



































































































































































































import Vue from "vue";
import ApiSearchPage from "@/components/apiSearch/components/ApiSearchPage.vue";
import CheckBox from "@/components/CheckBox.vue";
import MoneyDisplay from "@/components/MoneyDisplay.vue";
import BankPreAccFilterPanel from "../components/BankPreAccFilterPanel.vue";
import BankPreAccountingApi from "../services/BankPreAccountingApi";
import eventBus from "@/services/eventBus";
import auth from "@/services/auth";
import {
  BankPreAccError,
  BankPreAccLookups,
  BankPreAccRef,
  BankPreAccSetStatusResult,
  BankPreAccUpdatedStatus,
} from "../models/BankPreAccounting";
import { t } from "@/plugins/i18n";

interface Data {
  lookups: BankPreAccLookups | null;
  rowErrors: Record<string, BankPreAccError[]>;
  checkedRefs: BankPreAccRef[];
  reAccountRunning: boolean;
  /** A Kontírozás gomb alatti megerősítő doboz nyitva */
  reAccountConfirmOpen: boolean;
  statusActionRunning: boolean;
}

interface Methods {
  toggleChecked(item: any): void;
  openEdit(item: any): void;
  toggleErrors(item: any): Promise<void>;
  errorsFor(item: any): BankPreAccError[];
  syncCheckedItems(items: any[]): void;
  invalidateRowCaches(refs: BankPreAccRef[]): void;
  reAccountSelected(): Promise<void>;
  checkPermission(name: string): boolean;
  dropCheckedRefs(updated: BankPreAccUpdatedStatus[]): void;
  runStatusAction(action: () => Promise<void>): Promise<void>;
  setStatus(status: number): Promise<void>;
  bookSelected(): Promise<void>;
  takeBackSelected(status: number): Promise<void>;
  handleStatusResult(result: BankPreAccSetStatusResult, successMessage: string): void;
}

export default Vue.extend<Data, Methods, {}, {}>({
  components: {
    ApiSearchPage,
    CheckBox,
    MoneyDisplay,
    BankPreAccFilterPanel,
  },

  data() {
    return {
      lookups: null,
      rowErrors: {},
      checkedRefs: [],
      reAccountRunning: false,
      reAccountConfirmOpen: false,
      statusActionRunning: false,
    };
  },

  async beforeMount() {
    this.lookups = await BankPreAccountingApi.getLookups();
  },

  methods: {
    // K2D pb_modify: az "Adott bankkivonat kontírozási tételei" szerkesztő
    openEdit(item: any) {
      this.$router.push(`/bank-pre-accounting/${item.id}/edit`);
    },

    // Hibalista-nézet nyitása/zárása (⚠ gomb) — csak hibás kivonaton aktív
    async toggleErrors(item: any) {
      if (item.selected) {
        item.selected = false;
        return;
      }
      const key = `${item.id}`;
      if (!this.rowErrors[key]) {
        const errors = await BankPreAccountingApi.getErrors(item.id);
        Vue.set(this.rowErrors, key, errors);
      }
      item.selected = true;
    },

    errorsFor(item: any): BankPreAccError[] {
      return this.rowErrors[`${item.id}`] ?? [];
    },

    // A pipa-lista (K2D treff) lapozáson át is megmarad — ebből fut a csoportos kontírozás
    toggleChecked(item: any) {
      item.selectedStatus = !item.selectedStatus;
      const idx = this.checkedRefs.findIndex((x) => x.id === item.id);
      if (item.selectedStatus && idx < 0) {
        this.checkedRefs.push({ id: item.id });
      } else if (!item.selectedStatus && idx >= 0) {
        this.checkedRefs.splice(idx, 1);
      }
    },

    // Lapozás/újratöltés után a pipák visszaállítása (az ApiSearchPage @items eseményéből)
    syncCheckedItems(items: any[]): void {
      (items ?? []).forEach((item) => {
        const ref = this.checkedRefs.find((x) => x.id === item.id);
        if (ref && !item.selectedStatus) item.selectedStatus = true;
      });
    },

    // A lenyitó hibalista-cache a kontírozás után elavul — a következő nyitás frissen kéri le
    invalidateRowCaches(refs: BankPreAccRef[]) {
      refs.forEach((ref) => {
        Vue.delete(this.rowErrors, `${ref.id}`);
      });
    },

    // K2D pb_cont bank ága: a kijelölt kivonatok (újra)kontírozása egyetlen hívással —
    // a backend kivonatonként külön tranzakcióban fut, a kivonatonkénti hibák a
    // failures listában gyűlnek (K2D Continue), hard hibánál a feldolgozás megáll
    // (K2D Return → stopped). A K2D BBHUAKK megerősítő kérdése nincs portolva: a
    // gomb azonnal indít, a kimenet a jobb felső értesítési sávba megy.
    async reAccountSelected() {
      if (this.checkedRefs.length === 0) return;
      this.reAccountConfirmOpen = false;
      this.reAccountRunning = true;
      const refs = [...this.checkedRefs];
      try {
        const result = await BankPreAccountingApi.reAccount(refs.map((x) => x.id));

        const lines: string[] = [];
        if (result.failures.length > 0) {
          lines.push(t("AKovetkezoKivonatokKontirozasaSikertelenVolt"));
          result.failures.forEach((f) => lines.push(f));
        }
        if (result.stopped) {
          lines.push(t("AKontirozasHibanAlltMegA"));
        }
        if (lines.length > 0) {
          eventBus.$emit("error", lines.join("\n"));
        } else {
          eventBus.$emit("success", t("AKijeloltKivonatokUjrakontirozva"));
        }
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.reAccountRunning = false;
        // Hibaágon is újratöltünk — a kivonatonkénti tranzakciók részlegesen commitálhattak
        this.invalidateRowCaches(refs);
        eventBus.$emit("bank-pre-acc-statements:reload");
      }
    },

    checkPermission(name: string): boolean {
      const menuPerm = auth.menuPermissions.find((x) => x.name == name);
      return menuPerm?.isGranted ?? false;
    },

    // A sikeresen átállított kivonatok lekerülnek a kijelölésről (K2D dw_1.setItem
    // treff = ''), a hibára futottak kijelölve maradnak — így egy javítás után
    // közvetlenül újra futtatható rájuk a művelet
    dropCheckedRefs(updated: BankPreAccUpdatedStatus[]) {
      (updated ?? []).forEach((u) => {
        const idx = this.checkedRefs.findIndex((x) => x.id === u.id);
        if (idx >= 0) this.checkedRefs.splice(idx, 1);
      });
    },

    // A státusz-menüpontok azonnal futnak — a K2D Igen/Nem kérdéseit (BHNAFAAKK /
    // MFELADHATO / MFELADVA / BHVATAKBKFS / BHNFSTAKK) szándékosan nem portoltuk,
    // a művelet a kijelölt kivonatokon a menüpontra kattintva elindul
    async runStatusAction(action: () => Promise<void>) {
      if (this.statusActionRunning) return;
      this.statusActionRunning = true;
      const refs = [...this.checkedRefs];
      try {
        await action();
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.statusActionRunning = false;
        // Hibaágon is újratöltünk — a státusz állítás részlegesen commitálhatott
        this.invalidateRowCaches(refs);
        eventBus.$emit("bank-pre-acc-statements:reload");
      }
    },

    // A státusz-műveletek közös kimenet-kezelése: a kivonatonkénti hibák EGY összegző
    // értesítésben (K2D AKBKNTV), különben sikerüzenet. Mindkettő a jobb felső
    // értesítési sávba megy (az ErrorHandler pre-line-nal tördeli a sorokat), nincs
    // külön dialógus.
    handleStatusResult(result: BankPreAccSetStatusResult, successMessage: string) {
      this.dropCheckedRefs(result.updated);

      const lines: string[] = [];
      if (result.failures.length > 0) {
        lines.push(t("AKovetkezoKivonatokStatuszallitasaNemSikerult"));
        result.failures.forEach((f) => lines.push(f));
      }
      if (result.rolledBack) {
        lines.push(t("AMuveletNemHajtodottVegreEgyetlen"));
      }
      if (lines.length > 0) eventBus.$emit("error", lines.join("\n"));
      else eventBus.$emit("success", successMessage);
    },

    // K2D ue_acc_status Case 2/3/9 — csoportos státusz állítás
    setStatus(status: number) {
      if (this.checkedRefs.length === 0) return Promise.resolve();
      const successes: Record<number, string> = {
        2: t("AKijeloltKivonatokNemAdhatoFel"),
        3: t("AKijeloltKivonatokFeladhatoStatuszbaKerultek"),
        9: t("AKijeloltKivonatokNemFeladandoStatuszba"),
      };
      return this.runStatusAction(async () => {
        const result = await BankPreAccountingApi.setStatuses(
          status,
          this.checkedRefs.map((x) => x.id)
        );
        this.handleStatusResult(result, successes[status]);
      });
    },

    // K2D ue_acc_status Case 4 ("Fők.felé feladva") — feladás a belső főkönyvbe
    // (spmacc_bookhanding)
    bookSelected() {
      if (this.checkedRefs.length === 0) return Promise.resolve();
      return this.runStatusAction(async () => {
        // TODO: PREACCDIRECTPOST jogosultsági kulcs felvétele után a jog ténylegesen
        // kiosztható — addig a K2D alapesete él (nem Feladható kijelöltnél hibaüzenet).
        // A kulcs a számla előkontírozással közös ("egyből feladva jogosultság").
        const allowAutoPromote = this.checkPermission("PREACCDIRECTPOST");
        const result = await BankPreAccountingApi.book(
          this.checkedRefs.map((x) => x.id),
          allowAutoPromote
        );
        // A tárolt eljárás egy-egy kivonatot hibafile-ba is tehet (acc_status = 7)
        // anélkül, hogy a hívás hibára futna — ezért a végállapotot a lista
        // kontír státusz oszlopa (és a hibalista gombja) mutatja meg
        this.handleStatusResult(result, t("AKijeloltKivonatokFeladasaLefutott"));
      });
    },

    // K2D ue_acc_status "Case 5, 31" — a "Feladásra előkész." (5) és a "Vissza
    // feladhatóba" (31) menüpont ugyanerre a szerver-oldali ágra fut: a Feladható (3)
    // kivonat előkészítettbe kerül, a már feladott (4/6) pedig vissza a főkönyvből
    // (spjbookhandingdel1). Csak a sikerüzenet igazodik ahhoz, amit a felhasználó a
    // menüben választott.
    takeBackSelected(status: number) {
      if (this.checkedRefs.length === 0) return Promise.resolve();
      const success =
        status === 31
          ? t("AKijeloltKivonatokVisszaveveAFokonyvbol")
          : t("AKijeloltKivonatokFeladasraElokeszitveStatuszban");
      return this.runStatusAction(async () => {
        const result = await BankPreAccountingApi.takeBack(this.checkedRefs.map((x) => x.id));
        this.handleStatusResult(result, success);
      });
    },
  },

  computed: {
    // A lenyitott hibalista-nézet oszlopai — width nélkül, a table-layout: fixed
    // osztja el őket egyenlően a teljes szélességen
    errorColumns: () => [
      { field: "errTypeName", title: t("HibaTipusa") },
      { field: "note", title: t("Megjegyzes"), cell: "errorNoteTemplate" },
    ],

    // Az ApiSearchPage ebből építi a szerver felé menő filter/sort modellt és a Kendo grid
    // oszlopait — a K2D d_g_bankaccount oszlopsorrendje
    columns: () => [
      { cell: "commandTemplate", filterable: false, sortable: false, width: 120 },
      { field: "id", title: t("Azonosito"), width: 110, fieldClass: "right_text" },
      { field: "bankName", title: t("Bank") },
      { field: "accountNo", title: t("Bankszamlaszam"), width: 230 },
      { field: "extract", title: t("Kivonatszam"), width: 150, fieldClass: "right_text" },
      { field: "edate", title: t("KivonatDatuma"), width: 140, fieldClass: "right_text" },
      { field: "openBalance", title: t("NyitoEgyenleg_2"), cell: "openBalanceTemplate", filterable: false, sortable: false, width: 160 },
      { field: "closeBalance", title: t("ZaroEgyenleg_2"), cell: "closeBalanceTemplate", filterable: false, sortable: false, width: 160 },
      { field: "accStatusName", title: t("KonyvelesAllapot"), cell: "accStatusTemplate", width: 180 },
    ],
  },
});
